import { apiUrl } from "@/api/api";
import type { Persona } from "@/api/personas";
import { executePayment, mintToken } from "@/components/web3/TokenLaunchpad";
import { confirmTransaction } from "@/components/web3/TransactionUtils";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePrivy, useSolanaWallets } from "@privy-io/react-auth";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Form } from "../ui/form";
import MintTokenDetails from "./MintTokenDetails";
import MintTokenSubmitButton from "./MintTokenSubmitButton";
import MintTokenWallet from "./MintTokenWallet";

import { Loader2 } from "lucide-react";
import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { AlertDialog } from "../ui/alert-dialog";
import { useNavigate } from "react-router-dom";

const formSchema = z.object({
  tokenName: z.string().min(1, "Token name is required."),
  tokenSymbol: z
    .string()
    .min(1, "Token symbol is required.")
    .max(10, "Token symbol must be 10 characters or less"),
  description: z.string().min(1, "Description is required."),
  tokenImage: z.any(),
});

export type MintTokenFormSchema = z.infer<typeof formSchema>;

interface MintTokenFormProps {
  personaData: Persona;
}

const MintTokenForm: React.FC<MintTokenFormProps> = ({ personaData }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    personaData.avatar_id,
  );
  const queryClient = useQueryClient();

  const { wallets } = useSolanaWallets();
  const { login, authenticated } = usePrivy();

  const form = useForm<MintTokenFormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tokenName: personaData.web3?.metadata?.name || "",
      tokenSymbol: personaData.web3?.metadata?.symbol || "",
      description: personaData.short_bio || "No description available",
      tokenImage: undefined,
    },
  });

  const handleSubmit = async () => {
    let tokenMinted = false;

    if (!wallets || wallets.length === 0) {
      toast({
        variant: "destructive",
        description: "Please connect your wallet first",
      });
      return;
    }
    setIsLoading(true);

    const solWallet = wallets[0];

    try {
      if (!form.getValues("tokenImage")) {
        if (personaData.avatar_id === "") {
          toast({
            variant: "destructive",
            description: "Please upload an avatar image",
          });
          return;
        }

        const response = await fetch(
          `${apiUrl}/get-file?url=persona-knowledge-base/${personaData.persona_id}/${personaData.avatar_id}`,
        );
        const blob = await response.blob();
        form.setValue(
          "tokenImage",
          new File([blob], personaData.avatar_id, {
            type: blob.type,
          }),
        );
      }

      let imageFile;
      if (form.getValues("tokenImage") instanceof File) {
        imageFile = form.getValues("tokenImage");
      } else if (
        typeof form.getValues("tokenImage") === "string" &&
        personaData?.avatar_id
      ) {
        try {
          const response = await fetch(form.getValues("tokenImage"));
          const blob = await response.blob();
          imageFile = new File([blob], personaData.avatar_id, {
            type: blob.type,
          });
        } catch (error) {
          console.error("Error processing avatar image:", error);
          toast({
            variant: "destructive",
            description: "Error processing avatar image",
          });
          return;
        }
      }

      const promise = new Promise((resolve, reject) => {
        form.handleSubmit(
          (values) => resolve({ values }),
          (errors) => {
            console.error("invalid", errors, form.getValues());
            reject(errors);
          },
        )();
      });

      try {
        await promise;
      } catch {
        console.error("Form errors:", form.formState.errors);
        toast({
          variant: "destructive",
          description: "Please fix the form errors",
        });
        return;
      }

      const formData = new FormData();
      formData.append("image", imageFile!);
      formData.append("name", form.getValues("tokenName"));
      formData.append("symbol", form.getValues("tokenSymbol"));
      formData.append("description", form.getValues("description"));
      if (personaData.persona_id) {
        formData.append("personaId", personaData.persona_id);
      }

      try {
        // Execute payment
        const paymentSignature = await executePayment(
          solWallet,
          personaData.persona_id,
        );
        toast({ description: "Payment transaction sent" });

        // Wait for confirmation
        await confirmTransaction(paymentSignature);
        toast({ description: "Payment transaction confirmed" });

        // Mint token
        const mintTokenResult = await mintToken(
          solWallet,
          paymentSignature,
          formData,
        );

        console.log("mintTokenResult", mintTokenResult);

        tokenMinted = true;

        toast({ description: "Token successfully minted!" });
        navigate(`/agents/${personaData.persona_id}`);
      } catch (error: any) {
        console.error("Error creating token:", error);
        toast({
          variant: "destructive",
          description: `Error creating token: ${error.message}`,
        });
        throw error;
      }
    } catch (error: any) {
      console.error("Transaction error:", error);
      if (error.message?.includes("rejected")) {
        toast({
          variant: "destructive",
          description: "Transaction was cancelled. Please try again.",
        });
      } else {
        toast({
          variant: "destructive",
          description: `Transaction failed: ${error.message}`,
        });
      }
    } finally {
      setIsLoading(false);
      if (tokenMinted) {
        await queryClient.invalidateQueries({ queryKey: ["personas"] });
      }
    }
  };

  return (
    <>
      <div>
        <Form {...form}>
          <div className="space-y-4">
            <MintTokenDetails
              form={form}
              tokenImageUrl={avatarUrl}
              setTokenImageUrl={setAvatarUrl}
              personaData={personaData}
            />

            <MintTokenWallet
              authenticated={authenticated}
              wallets={wallets}
              login={login}
              data-mint-page-tour="connect-wallet"
            />

            <MintTokenSubmitButton
              isLoading={isLoading}
              onClick={handleSubmit}
              buttonText="Mint Token with Pool"
              data-mint-page-tour="submit-button"
            />
          </div>
        </Form>
      </div>

      <AlertDialog open={isLoading}>
        <AlertDialogContent className="bg-darkGray border-none">
          <div className="flex flex-col items-center justify-center p-6">
            <Loader2 className="w-20 h-20 animate-spin text-primary" />
            <AlertDialogTitle className="mt-4">
              Processing Transaction
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm text-muted-foreground">
              Please wait while we process your transaction. This may take a few
              moments. Please make sure you approve the transaction in your
              wallet.
            </AlertDialogDescription>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MintTokenForm;
